import { Component, computed, output, signal } from '@angular/core';
import { CryptoUUID, groupBy } from '@umss/shared/utils';

import { UmssAlertComponent } from '../alert/alert.component';
import { ToastAlertConfig, ToastAlertPosition } from './toast.model';

export type ToastAlert = ToastAlertConfig & {
  id: CryptoUUID;
};

@Component({
  selector: 'umss-toast',
  imports: [UmssAlertComponent],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  animations: []
})
export class UmssToastComponent {
  /**
   * Store for the active alerts in the toast component
   */
  alerts = signal<ToastAlert[]>([]);

  /**
   * Event emitted when all alerts have been closed
   */
  allClose = output<void>();

  /**
   * Available positions for the toast alerts
   */
  readonly positions: ToastAlertPosition[] = [
    'top-left',
    'top',
    'top-right',
    'bottom-left',
    'bottom',
    'bottom-right'
  ];

  /**
   * Groups the alerts by their position for rendering purposes
   */
  alertsGroupedByPosition = computed<Map<ToastAlertPosition, ToastAlert[]>>(
    () => {
      if (this.alerts().length === 0) {
        return new Map<ToastAlertPosition, ToastAlert[]>();
      }
      const g = groupBy<ToastAlert, ToastAlertPosition>(
        this.alerts(),
        (alert) => alert.position
      );
      return g;
    }
  );

  /**
   * Stores the close intervals for each alert
   */
  closeIntervals = signal<Record<CryptoUUID, ReturnType<typeof setInterval>>>(
    {}
  );

  /**
   * Store ids of the closed alerts to manage their animation
   */
  closingAlerts = signal<Set<CryptoUUID>>(new Set<CryptoUUID>());

  /**
   * Adds a new alert to the toast component
   * @param config : ToastAlertConfig - Configuration of the alert to be added
   */
  addAlert(config: ToastAlertConfig) {
    const id = crypto.randomUUID();
    this.alerts.update((store) => {
      return [{ ...config, id }, ...store];
    });
    this.scheduleDelete(id, config);
  }

  /**
   * Handle the manual removal of an alert
   * @param id : CryptoUUID - Id of the alert to be removed
   */
  removeAlert(id: CryptoUUID) {
    this.closingAlerts.update((store) => store.add(id));
    this.clearAlertInterval(id);

    setTimeout(() => {
      this.alerts.update((store) => {
        const copy = store.filter((alert) => alert.id !== id);
        return copy;
      });

      this.closingAlerts.update((store) => {
        store.delete(id);
        return store;
      });
      if (this.alerts().length === 0) {
        this.allClose.emit();
      }
    }, 250);
  }

  /**
   * Schedules the automatic deletion of an alert after its duration
   * @param id :  CryptoUUID - Id of the alert
   * @param config : ToastAlertConfig - Configuration of the alert
   */
  private scheduleDelete(id: CryptoUUID, config: ToastAlertConfig) {
    const interval = setInterval(() => {
      this.removeAlert(id);
    }, config.duration);
    this.closeIntervals.update((store) => ({
      ...store,
      [id]: interval
    }));
  }

  /**
   * Manages the cleanup of the close interval when an alert is closed
   * @param id : CryptoUUID - Id of the alert
   */
  private clearAlertInterval(id: CryptoUUID) {
    const currentIntervals = this.closeIntervals();
    if (currentIntervals[id]) {
      clearInterval(currentIntervals[id]);
      // Omit the deleted interval from the store
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _, ...rest } = currentIntervals;
      this.closeIntervals.set(rest);
    }
  }
}
